// The Washing Plan's rules and data (packages/shared domain/wash.ts): which boats need which wash, from the
// Overview (production) and the Service sheet (service), merged with the plan kept in the app.
//   production, once a boat is at 96% or more with its engine installed (and hasn't left):
//     1. after the sea trial: ready once Sea trial is Yes; before that it waits on the trial (and can be
//        planned for the day after it). No longer needed once the second wash is done or it is polished.
//     2. pre-delivery, before polishing: until Polishing is Yes (or the Overview's Washing is Yes); due by the
//        delivery date, ready once the sea trial is done.
//     3. re-wash: when the second wash was done more than `rewashAfterDays` before a delivery still to come;
//        due by the delivery date.
//   a boat carried over from last season shows no completion on this season's Overview (its % lives on last
//   season's production sheets): with its engine installed it counts as a finished boat. It was washed (and
//   polished) last season, so it is re-washed before its delivery, and gets the washes above that the
//   Overview still calls for.
//   service: a vessel registered this season (as the Service page shows it: features/serviceSeason.ts) at the
//     yard (@ Shipyard Yes / Completed) whose works are all closed (Completed or N/A, or a Done tick), or
//     marked Completed; due by its delivery to the client (the sheet's Delivery date when still to come,
//     else its Expected delivery date). The sheet keeps past seasons' visits too, some never marked
//     Delivered: those are left out. A boat is one boat: of its visits this season (features/serviceVisits.ts)
//     the newest decides, and an older one left open on the sheet asks for nothing, while the newest is still
//     in service or after its wash; a wash already planned on an older visit stays on the plan, and once done
//     it counts for the newest visit too.
// A wash done is no longer needed, nor one marked "no washing required". Everything is plain 'YYYY-MM-DD'
// days, never shifted through a time zone. How long a wash takes comes from the boat's model (WASH_TIMES).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assemblyPctNum, engineInstalled, findServiceStatusColumn, MODEL_ORDER, REWASH_AFTER_DAYS, splitBoatTitle, WASH_MIN_PCT, washId,
  type OverviewVessel, type WashInput, type WashKind, type WashMinutes, type WashRecord, type WashSource,
} from '@technohull/shared';
import { api } from '../lib/api.js';
import { localTodayIso } from '../lib/dates.js';
import type { SheetGrid, SheetGridRow } from './hooks.js';
import { serviceVesselDateColumns } from './serviceDates.js';
import { serviceStatusCloses } from './serviceStatus.js';
import { isCurrentServiceSeason, serviceSeasonColumns, serviceVesselSeason, serviceVisitDates } from './serviceSeason.js';
import { boatIdentity, compareVisits } from './serviceVisits.js';

/** why a wash is needed, for the page (and the printout) to put in words */
export type WashWhy =
  | { k: 'seaDone'; date: string }
  | { k: 'seaOn'; date: string }
  | { k: 'seaPending' }
  | { k: 'beforePolish'; delivery: string }
  | { k: 'rewash'; washed: string; delivery: string; days: number }
  | { k: 'lastSeason'; delivery: string }
  | { k: 'serviceDone'; delivery: string };

/** a boat that needs (or had) a wash, with its plan */
export interface WashItem {
  id: string;
  source: WashSource;
  boatKey: string;
  kind: WashKind;
  name: string;
  model: string;
  sap: string;
  /** assembly %, production boats only (null for a boat carried over from last season, which has none) */
  pct: number | null;
  /** the day the boat goes to the client ('' when not known) */
  delivery: string;
  why: WashWhy;
  /** it can be washed now (production: the sea trial is done); otherwise it waits on something */
  ready: boolean;
  /** the last day it should be washed by ('' when there's no deadline) */
  dueBy: string;
  record?: WashRecord;
}

const yes = (v: unknown) => String(v ?? '').trim().toLowerCase() === 'yes';
const dayOf = (v: unknown): string => {
  const m = typeof v === 'string' ? /^\s*(\d{4}-\d{2}-\d{2})/.exec(v) : null;
  return m ? m[1]! : '';
};
const DAY_MS = 86_400_000;
/** whole days from a to b ('YYYY-MM-DD') */
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);

/** minutes in words: "1h 30m" ("1ώ 30λ" in Greek); `tight` for a small space: "1h30" */
export function minutesText(m: number, el = false, tight = false): string {
  const h = Math.floor(m / 60), min = m % 60;
  const H = el ? 'ώ' : 'h', M = el ? 'λ' : 'm';
  if (!h) return `${min}${M}`;
  if (!min) return `${h}${H}`;
  return tight ? `${h}${H}${String(min).padStart(2, '0')}` : `${h}${H} ${min}${M}`;
}
/** a washing time: "2h 30m–3h", or one figure when it doesn't vary */
export const washRangeText = ([a, b]: WashMinutes, el = false, tight = false): string =>
  a === b ? minutesText(a, el, tight) : `${minutesText(a, el, tight)}–${minutesText(b, el, tight)}`;

/** the production washes the Overview calls for. */
export function productionWashes(
  vessels: readonly OverviewVessel[],
  records: ReadonlyMap<string, WashRecord>,
  today: string,
  rewashAfterDays = REWASH_AFTER_DAYS,
): WashItem[] {
  const out: WashItem[] = [];
  // the season's first day (1 Sep): a carried-over boat's washes done (or let off) before it are last season's
  const year = Number(today.slice(0, 4));
  const seasonStart = `${Number(today.slice(5, 7)) >= 9 ? year : year - 1}-09-01`;
  const lastSeasons = (r?: WashRecord) => !!r && ((!!r.doneOn && r.doneOn < seasonStart) || (!!r.notNeededOn && r.notNeededOn < seasonStart));
  for (const v of vessels) {
    const sap = String(v.sap ?? '').trim();
    if (!sap || v.left) continue;
    const pct = assemblyPctNum(v);
    if (!engineInstalled(v)) continue;
    // last season's boat: no completion on this season's Overview (blank or 0%), but its engine is in
    const carried = pct === 0;
    if (!carried && pct < WASH_MIN_PCT) continue;
    const rec = (k: WashKind) => {
      const r = records.get(washId('prod', sap, k));
      return carried && lastSeasons(r) ? undefined : r;
    };
    const r1 = rec('seatrial'), r2 = rec('predelivery'), r3 = rec('rewash');
    // done, or marked not needed: nothing left to do for that wash
    const closed = (r?: WashRecord) => !!(r?.doneOn || r?.notNeededOn);
    const delivery = dayOf(v.deliveryDate);
    const polished = yes(v.pol);
    const seaDone = yes(v.seaTrial);
    const seaDate = dayOf(v.seaTrialDate);
    const base = { source: 'prod' as const, boatKey: sap, name: String(v.name ?? '').trim() || sap, model: String(v.model ?? '').trim(), sap, pct: carried ? null : pct, delivery };
    const item = (kind: WashKind, why: WashWhy, ready: boolean, dueBy: string): WashItem => ({ ...base, id: washId('prod', sap, kind), kind, why, ready, dueBy, record: rec(kind) });

    if (!closed(r1) && !r2?.doneOn && !polished) {
      out.push(item('seatrial', seaDone ? { k: 'seaDone', date: seaDate } : seaDate ? { k: 'seaOn', date: seaDate } : { k: 'seaPending' }, seaDone, ''));
    }
    if (!closed(r2) && !polished && !yes(v.wa)) {
      out.push(item('predelivery', { k: 'beforePolish', delivery }, seaDone, delivery));
    }
    if (r2?.doneOn && !closed(r3) && delivery && delivery >= today) {
      const days = daysBetween(r2.doneOn, delivery);
      if (days > rewashAfterDays) out.push(item('rewash', { k: 'rewash', washed: r2.doneOn, delivery, days }, true, delivery));
    } else if (carried && !closed(r2) && !closed(r3) && (polished || yes(v.wa))) {
      // washed last season: wash it again before it goes, while it's still here (even past its delivery date)
      out.push(item('rewash', { k: 'lastSeason', delivery }, true, delivery));
    }
  }
  return out;
}

const DONE_TEXT = /complete|done|closed|✔|✓|delivered|ready|ολοκλ|τελείωσ|έτοιμ/i;
const MODELS = new Set<string>(MODEL_ORDER);

/** what the Washing Plan reads of one Service sheet vessel: its name, model (when the name starts with one),
 *  SAP and delivery to the client (the sheet's Delivery date when still to come, else its Expected delivery
 *  date), its service season and whether that's a current registration (`currentSeason`, as the Service page
 *  tells), whether it's at the yard (@ Shipyard Yes / Completed) and whether its service is finished (its
 *  works all closed: Completed or N/A, or a Done tick; or it's marked Completed). */
export interface ServiceWashVessel {
  key: string; name: string; model: string; sap: string; delivery: string;
  season: string; current: boolean; here: boolean; finished: boolean;
  /** the visit's first date ('' when it has none), to tell a boat's visits apart in time */
  from: string;
}

/** every vessel on the Service sheet (a row right under a top-level group), by its row id. */
export function serviceWashVessels(grid: SheetGrid, today: string, currentSeason: string): Map<string, ServiceWashVessel> {
  const out = new Map<string, ServiceWashVessel>();
  const cols = grid.columns;
  const primary = cols.find((c) => c.primary) ?? cols[0];
  if (!primary) return out;
  const yardCol = cols.find((c) => !c.primary && /shipyard|ναυπηγ/i.test(c.title));
  const statusCol = findServiceStatusColumn(cols);
  const doneCol = cols.find((c) => c.type === 'CHECKBOX' && /done|complete|✓|ok|ολοκλ|finish|τελ/i.test(c.title));
  const sapCol = cols.find((c) => !c.primary && /\bsap\b/i.test(c.title));
  const dates = serviceVesselDateColumns(cols);
  const seasonCols = serviceSeasonColumns(cols);
  const text = (r: SheetGridRow, id: number | undefined) => (id == null ? '' : typeof r.cells[id] === 'string' ? String(r.cells[id]).trim() : '');

  const byId = new Map(grid.rows.map((r) => [r.id, r]));
  const kids = new Map<number, SheetGridRow[]>();
  for (const r of grid.rows) if (r.parentId != null) (kids.get(r.parentId) ?? kids.set(r.parentId, []).get(r.parentId)!).push(r);
  const named = (r: SheetGridRow) => !!text(r, primary.id);
  // a vessel's works: its rows with no rows under them (a work split into sub-works counts by those)
  const leaves = (id: number): SheetGridRow[] =>
    (kids.get(id) ?? []).filter(named).flatMap((k) => ((kids.get(k.id) ?? []).some(named) ? leaves(k.id) : [k]));
  const closed = (r: SheetGridRow) => {
    if (doneCol && r.cells[doneCol.id] === true) return true;
    const s = statusCol ? text(r, statusCol.id) : '';
    return !!s && (serviceStatusCloses(s) || DONE_TEXT.test(s));
  };

  for (const v of grid.rows) {
    // a vessel sits right under a top-level group row
    const group = v.parentId != null ? byId.get(v.parentId) : undefined;
    if (!group || group.parentId != null || !named(v)) continue;
    const yard = yardCol ? (v.cells[yardCol.id] === true ? 'Yes' : text(v, yardCol.id)) : '';
    const completed = /complet|ολοκλ/i.test(yard);
    const works = leaves(v.id);
    const actual = dates.completion ? dayOf(v.cells[dates.completion.id]) : '';
    const planned = dates.expectedCompletion ? dayOf(v.cells[dates.expectedCompletion.id]) : '';
    const name = text(v, primary.id);
    const lead = splitBoatTitle(name).model.toUpperCase();
    const season = serviceVesselSeason(v, (id) => kids.get(id) ?? [], seasonCols);
    out.set(String(v.id), {
      key: String(v.id), name, model: MODELS.has(lead) ? lead : '',
      sap: sapCol ? text(v, sapCol.id).replace(/^(\d+)\.0+$/, '$1') : '',
      delivery: actual && actual >= today ? actual : planned || actual,
      season: season?.id ?? '', current: isCurrentServiceSeason(season, currentSeason),
      here: completed || /^(yes|y|ναι|true)$/i.test(yard),
      finished: completed || (works.length > 0 && works.every(closed)),
      from: serviceVisitDates(v, seasonCols)[0] ?? '',
    });
  }
  return out;
}

/** the service boats registered this season that the Service sheet says are finished and still to go back to
 *  their clients (not washed, and not marked as needing no wash), each boat once. A boat is one boat: its newest
 *  visit this season decides, whatever an older one left open on the sheet says. It is asked for once that
 *  visit is at the yard and finished, unless its wash is done or not needed, or a wash on another visit's record
 *  was done (or called not needed) since that visit began. A wash already planned on a visit at the yard and
 *  finished stays on the plan. */
export function serviceWashes(grid: SheetGrid, records: ReadonlyMap<string, WashRecord>, today: string, currentSeason: string): WashItem[] {
  const recordOf = (v: ServiceWashVessel) => records.get(washId('service', v.key, 'service'));
  const visits = new Map<string, Array<{ v: ServiceWashVessel; at: number }>>();
  [...serviceWashVessels(grid, today, currentSeason).values()].forEach((v, at) => {
    if (!v.current) return;
    const k = boatIdentity(v);
    (visits.get(k) ?? visits.set(k, []).get(k)!).push({ v, at });
  });
  const byBoat = new Map<string, ServiceWashVessel>();
  for (const [k, list] of visits) {
    const open = (v: ServiceWashVessel) => { const r = recordOf(v); return !r?.doneOn && !r?.notNeededOn; };
    const due = (v: ServiceWashVessel) => v.here && v.finished;
    const ordered = list.sort((a, b) => compareVisits({ ...a.v, at: a.at }, { ...b.v, at: b.at })).map((x) => x.v);
    const newest = ordered.at(-1)!;
    // washed (or let off) on an older visit's record while the newest was already under way
    const settled = !!newest.from && ordered.some((v) => {
      const r = v !== newest ? recordOf(v) : undefined;
      return !!r && ((!!r.doneOn && r.doneOn >= newest.from) || (!!r.notNeededOn && r.notNeededOn >= newest.from));
    });
    const planned = ordered.filter((v) => due(v) && open(v) && recordOf(v)?.plannedOn).at(-1);
    const pick = planned ?? (due(newest) && open(newest) && !settled ? newest : undefined);
    if (pick) byBoat.set(k, pick);
  }
  const out: WashItem[] = [];
  for (const v of byBoat.values()) {
    const record = records.get(washId('service', v.key, 'service'));
    out.push({
      id: washId('service', v.key, 'service'), source: 'service', boatKey: v.key, kind: 'service',
      name: v.name, model: v.model, sap: v.sap,
      pct: null, delivery: v.delivery, why: { k: 'serviceDone', delivery: v.delivery }, ready: true, dueBy: v.delivery, record,
    });
  }
  return out;
}

/** what the page knows of a boat, by `${source}:${boatKey}`: every production boat on the Overview (so one whose
 *  washes are all done still reads in full), and the service boats from their washes. */
export interface WashBoat { model: string; sap: string; pct: number | null; delivery: string }
export function washBoats(vessels: readonly OverviewVessel[], items: readonly WashItem[]): Map<string, WashBoat> {
  const out = new Map<string, WashBoat>();
  for (const v of vessels) {
    const sap = String(v.sap ?? '').trim();
    const pct = assemblyPctNum(v);
    // a boat from last season has no completion on this season's Overview: none to show
    if (sap) out.set(`prod:${sap}`, { model: String(v.model ?? '').trim(), sap, pct: pct === 0 && engineInstalled(v) ? null : pct, delivery: dayOf(v.deliveryDate) });
  }
  for (const i of items) if (!out.has(`${i.source}:${i.boatKey}`)) out.set(`${i.source}:${i.boatKey}`, { model: i.model, sap: i.sap, pct: i.pct, delivery: i.delivery });
  return out;
}

/** the washes to plan (not planned yet, not done, not marked not needed), soonest deadline first (none last),
 *  ready ones first. */
export function washQueue(items: readonly WashItem[]): WashItem[] {
  return items
    .filter((i) => !i.record?.plannedOn && !i.record?.doneOn && !i.record?.notNeededOn)
    .sort((a, b) => Number(b.ready) - Number(a.ready) || (a.dueBy || '9999') .localeCompare(b.dueBy || '9999') || a.name.localeCompare(b.name));
}

/** what the calendar shows on each day of a month ('YYYY-MM'): the washes planned for it (or, once done, on
 *  the day they were done), from the plan itself, so a wash stays on the plan after its boat has left. */
export function washesByDay(records: readonly WashRecord[], items: ReadonlyMap<string, WashItem>, month: string): Map<string, Array<{ record: WashRecord; item?: WashItem }>> {
  const out = new Map<string, Array<{ record: WashRecord; item?: WashItem }>>();
  for (const r of records) {
    const day = r.doneOn || r.plannedOn;
    if (!day || !day.startsWith(month)) continue;
    const list = out.get(day) ?? [];
    list.push({ record: r, item: items.get(r.id) });
    out.set(day, list);
  }
  for (const list of out.values()) list.sort((a, b) => a.record.kind.localeCompare(b.record.kind) || a.record.boatName.localeCompare(b.record.boatName));
  return out;
}

// ── data ───────────────────────────────────────────────────────────────────────────────────────────────
const KEY = ['washes'] as const;
type Washes = { washes: WashRecord[] };

export function useWashes(enabled = true) {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<Washes>('/api/washes'), enabled, staleTime: 15_000, refetchOnWindowFocus: true });
}

/** plan, move, take off, tick or note a wash. The change shows straight away; the server's record follows. */
export function useSetWash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: WashInput) => api.post<{ ok: true; wash: WashRecord | null }>('/api/washes', v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<Washes>(KEY);
      const id = washId(v.source, v.boatKey, v.kind);
      qc.setQueryData<Washes>(KEY, (d) => {
        const list = d?.washes ?? [];
        const cur = list.find((w) => w.id === id) ?? {
          id, source: v.source, boatKey: v.boatKey.trim(), kind: v.kind, boatName: v.boatName ?? '', boatModel: v.boatModel ?? '',
          plannedOn: '', doneOn: '', doneBy: '', notNeededOn: '', notNeededBy: '', note: '', updatedBy: '', updatedAt: '',
        };
        // as the server applies it: not needed takes the wash off the plan and undoes a done tick; a day or a
        // done tick means it's needed after all
        const needed = { notNeededOn: '', notNeededBy: '' };
        const next: WashRecord = {
          ...cur,
          ...(v.notNeeded ? { notNeededOn: localTodayIso(), notNeededBy: '', plannedOn: '', doneOn: '', doneBy: '' } : v.notNeeded === false ? needed : {}),
          ...(v.plannedOn !== undefined ? { plannedOn: v.plannedOn ?? '', ...(v.plannedOn ? needed : {}) } : {}),
          ...(v.done !== undefined ? (v.done ? { doneOn: v.doneOn ?? (cur.plannedOn || localTodayIso()), ...needed } : { doneOn: '', doneBy: '' }) : {}),
          ...(v.note !== undefined ? { note: v.note } : {}),
        };
        const keep = next.plannedOn || next.doneOn || next.notNeededOn || next.note;
        return { washes: keep ? [...list.filter((w) => w.id !== id), next] : list.filter((w) => w.id !== id) };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(KEY, ctx.prev); },
    onSettled: () => { void qc.invalidateQueries({ queryKey: KEY }); },
  });
}
